import { i18nFilter } from "app/filters/i18nFilter";
import { percentageSignOnlyFilter } from "app/filters/ngFilters";
import type { FunctionComponent } from "react";
import React, { useMemo, useState } from "react";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { formatGenderPieChartData, getGenderPieChartConfig, getGenderPieChartStyle } from "./genderPiechartConfig";
import {
    TitleContainer,
    StyledHeaderTitle,
    GenderChartContainer,
    StyledChart,
    LoaderContainer,
} from "./StyledComponents";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import { ButtonsContainer } from "pro-features/components/Rule/src/styledComponents";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { PdfExportService } from "app/services/PdfExportService";
import { allTrackers } from "app/services/track/track";
import { TableNoData } from "app/components/React/Table/FlexTable/Big/FlexTableStatelessComponents";
import { Loader } from "app/pages/website-analysis/traffic-sources/mmx/components/common/Loader";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { getExcelDownloadTracking, getExelLink } from "app/pages/website-analysis/demographics/utils";
import { swSettings } from "app/common/services/swSettings";

interface IGenderDistributionPieChartProps {
    malePercent: number;
    femalePercent: number;
    isLoading?: boolean;
    disableAddToDashboard?: boolean;
    downloadExcelUrl?: string;
}

export const GenderDistributionPieChart: FunctionComponent<IGenderDistributionPieChartProps> = (props) => {
    const { malePercent, femalePercent, isLoading = false, disableAddToDashboard = false, downloadExcelUrl } = props;
    const downloadExcelPermitted = swSettings.current.resources.IsExcelAllowed;

    const isEmpty = isNaN(malePercent) && isNaN(femalePercent);

    const { chartConfig, chartStyle } = useMemo(() => {
        return {
            chartConfig: getGenderPieChartConfig({ type: "pie", filter: percentageSignOnlyFilter }),
            chartStyle: getGenderPieChartStyle(),
        };
    }, []);

    const params = proNavigator.getParams() as any;
    const apiParams = proNavigator.getApiParams(params);
    const [chartRef, setChartRef] = useState(null);

    const getPNG = React.useCallback((): Promise<void> => {
        allTrackers.dispatchTrackEvent("Gender_Distribution.png", "submit-ok", "Gender_Distribution.png");
        const offSetX = 0;
        const offSetY = 50;
        const fileName = `Website Analysis Gender-Distribution-(${apiParams.key})-(from: ${apiParams.from})-(to: ${apiParams.to})`;
        const styleHTML = PdfExportService.getStylesheetsForPng();
        return PdfExportService.downloadHtmlPngFedService(
            styleHTML + chartRef.current.outerHTML,
            fileName,
            chartRef.current.offsetWidth + offSetX,
            chartRef.current.offsetHeight + offSetY,
        );
    }, [chartRef]);

    const chartData = useMemo(() => {
        return formatGenderPieChartData(malePercent, femalePercent);
    }, [malePercent, femalePercent]);

    const getUtilityButtons = () => {
        return (
            <ButtonsContainer>
                <DownloadButtonMenu
                    PNG={true}
                    handleDownloadFunction={getPNG}
                />
                {!disableAddToDashboard && (
                    <AddToDashboard
                        modelType="fromWebsite"
                        webSource={"Total"}
                        type="PieChart"
                        metric={"WebDemographicsGender"}
                    />
                )}
                <DownloadButtonMenu
                    Excel={true}
                    downloadUrl={downloadExcelUrl ?? getExelLink(params)}
                    excelLocked={!downloadExcelPermitted}
                    trackingFunction={getExcelDownloadTracking("Gender")}
                />
            </ButtonsContainer>
        );
    };

    return (
        <GenderChartContainer ref={chartRef}>
            <TitleContainer>
                <FlexColumn>
                    <StyledHeaderTitle>
                        <BoxTitle tooltip={i18nFilter()("webdemographics.piechart.title.tooltip")}>
                            {i18nFilter()("dashboard.metricGallery.Website.DesktopDemographicsGender.title")}
                        </BoxTitle>
                    </StyledHeaderTitle>
                </FlexColumn>
                {!isEmpty && getUtilityButtons()}
            </TitleContainer>
            {isLoading ? (
                <LoaderContainer>
                    <Loader />
                </LoaderContainer>
            ) : (
                <>
                    {isEmpty ? (
                        <TableNoData
                            icon="no-data"
                            messageTitle={i18nFilter()("global.nodata.notavilable")}
                        />
                    ) : (
                        <StyledChart
                            type={"pie"}
                            config={chartConfig}
                            data={chartData}
                            domProps={chartStyle}
                            afterRender={(chart) => {
                                setChartRef(chart);
                                return {};
                            }}
                        />
                    )}
                </>
            )}
        </GenderChartContainer>
    );
};
